import React, { useState, useEffect } from 'react';
import { Phone, Calendar, Menu, X, Search } from 'lucide-react';
import { Button } from './ui/button';
import { contactInfo } from '../data/mock';
import SearchModal from './SearchModal';
import { trackPhoneClick } from '../utils/analytics';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Header = ({ onBookingClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [themeSettings, setThemeSettings] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch theme settings
  useEffect(() => {
    const fetchThemeSettings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/theme-settings`);
        setThemeSettings(response.data);
      } catch (error) {
        console.error('Error fetching theme settings:', error);
      }
    };
    fetchThemeSettings();
  }, []);

  // Get logo URL based on theme mode
  const getLogoUrl = () => {
    const darkModeLogo = themeSettings?.logos?.dark_mode;
    
    // If we have a logo from theme settings, use it
    if (darkModeLogo) {
      // Handle relative URLs
      if (darkModeLogo.startsWith('/') && !darkModeLogo.startsWith('/api')) {
        return darkModeLogo;
      }
      // Handle API URLs
      if (darkModeLogo.startsWith('/api')) {
        return `${BACKEND_URL}${darkModeLogo}`;
      }
      // Handle absolute URLs
      return darkModeLogo;
    }
    
    // Fallback to default logo (the new Sand-tec logo)
    return '/images/sand-tec-logo-light.png';
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-gray-900/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Clickable link to homepage */}
          <a href="/" className="flex items-center space-x-3 pr-[100px] cursor-pointer" title="Gå til forsiden">
            <img 
              src={getLogoUrl()}
              alt="Sand-tec AS Logo"
              className="h-16 w-auto object-contain"
              onError={(e) => {
                // Fallback to local logo if remote fails
                e.target.src = '/images/sand-tec-logo-light.png';
              }}
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <a
              href="/tjenester"
              className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
            >
              Tjenester
            </a>
            <a
              href="/aktuelt"
              className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
            >
              Aktuelt
            </a>
            <a
              href="/kampanjer"
              className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
            >
              Kampanjer
            </a>
            <a
              href="/team"
              className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
            >
              Team
            </a>
            <a
              href="/anmeldelser"
              className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
            >
              Anmeldelser
            </a>
            <a
              href="/referanser"
              className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
            >
              Referanser
            </a>
            <a
              href="/kontakt"
              className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
            >
              Kontakt
            </a>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-300 hover:text-white hover:bg-gray-800"
              onClick={() => setIsSearchOpen(true)}
            >
              <Search className="w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              className="border-2 border-gray-400 text-white hover:bg-gray-400 hover:text-black transition-all duration-300"
              onClick={() => {
                trackPhoneClick(contactInfo.phone, 'header');
                window.location.href = `tel:${contactInfo.phone}`;
              }}
            >
              <Phone className="w-4 h-4 mr-2" />
              Ring oss
            </Button>
            <Button
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold transition-all duration-300 shadow-lg hover:shadow-red-500/50"
              onClick={onBookingClick}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Book nå
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-800 bg-gray-900">
            <nav className="flex flex-col space-y-4">
              <a
                href="/tjenester"
                className="text-left text-gray-300 hover:text-white transition-colors duration-200 font-medium"
              >
                Tjenester
              </a>
              <a
                href="/aktuelt"
                className="text-left text-gray-300 hover:text-white transition-colors duration-200 font-medium"
              >
                Aktuelt
              </a>
              <a
                href="/kampanjer"
                className="text-left text-gray-300 hover:text-white transition-colors duration-200 font-medium"
              >
                Kampanjer
              </a>
              <a
                href="/team"
                className="text-left text-gray-300 hover:text-white transition-colors duration-200 font-medium"
              >
                Team
              </a>
              <a
                href="/anmeldelser"
                className="text-left text-gray-300 hover:text-white transition-colors duration-200 font-medium"
              >
                Anmeldelser
              </a>
              <a
                href="/referanser"
                className="text-left text-gray-300 hover:text-white transition-colors duration-200 font-medium"
              >
                Referanser
              </a>
              <a
                href="/kontakt"
                className="text-left text-gray-300 hover:text-white transition-colors duration-200 font-medium"
              >
                Kontakt
              </a>
              <div className="flex flex-col space-y-2 pt-4">
                <Button
                  variant="outline"
                  className="border-2 border-gray-400 text-white hover:bg-gray-400 hover:text-black w-full"
                  onClick={() => window.location.href = `tel:${contactInfo.phone}`}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Ring oss
                </Button>
                <Button
                  className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold w-full"
                  onClick={onBookingClick}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book nå
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>

      {/* Racing Stripe */}
      <div className="h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent"></div>
      
      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </header>
  );
};

export default Header;